import { LocalAtRecord, Mission, Session, Job, Attachment } from '../../../db';
import { kmz2kml } from '../../../utils';
import { alertError, alertFullScreen, alertWarn } from '../../../alertDispatcher';
import { ShowAutoGenHelper, RogoShapefile, RogoShapefileType, JobType } from '../../../types/types';
import { JobsFieldSet } from '@rogoag/airtable/crm';
import { DataSourceOption, IMissionLoader, mapMakingLock } from '../MissionLoaderCommon';
import { createMission } from '../../../db_ops/session_ops';
import { MissionState } from '../../../constants';
import logger from '../../../logger';
import { SupportedFileExtensions } from '../../ShapefileParserService';

class OriginalMissionLoader implements IMissionLoader {
  async load(
    session: Session,
    jobID: string,
    sampling: boolean,
    setAutogenHelper?: ShowAutoGenHelper,
  ): Promise<Mission | undefined> {
    if (!jobID) {
      alertError('Job ID not found');

      return;
    }

    const job = await Job.getJobRecordById(jobID);

    if (!job) {
      alertError('Job not found');

      return;
    }

    let dataSource: DataSourceOption = 'Shapefiles';

    if (!sampling) {
      dataSource = await mapMakingLock(job);

      if (dataSource === 'Cancel Load') {
        return;
      }
    }

    if (!job) {
      console.error(`Job not found: ${jobID}`);
      alertError('Job not found');

      return;
    }

    if (!sampling) {
      return await this.handleNonSamplingCase(dataSource, session, job, jobID, sampling, setAutogenHelper);
    } else {
      return await this.handleSamplingCase(job, session, jobID, sampling);
    }
  }

  private async handleSamplingCase(
    job: LocalAtRecord<JobsFieldSet>,
    session: Session,
    jobID: string,
    sampling: boolean,
  ): Promise<Mission | undefined> {
    const missionStatus = job.get('Mission Status');

    // If job status is not "Mission Finalized", show alert with a confirmation dialog
    if (missionStatus !== MissionState._4_Mission_Finalized) {
      const options = ['I CALLED OPS', 'Cancel'];
      const proceed = await alertFullScreen(
        'Attention!',
        'Potentially Invalid Mission - Must Call Ops',
        options,
        '#FFCC00',
      );

      if (proceed !== 'I CALLED OPS') {
        return;
      }

      await logger.log(
        'OriginalMissionLoader - handleSamplingCase',
        `User proceeded with loading a non-finalized mission for sampling. Job ID: ${jobID}, Mission Status: ${missionStatus}`,
      );
    }

    let kmlFile: Attachment | undefined;

    try {
      kmlFile = (await job.get_attachments('Partial Mission'))[0];

      if (!kmlFile) {
        kmlFile = (await job.get_attachments('Int Mission'))[0];
      }
    } catch (e) {
      console.error('Error getting attachments for sampling mission', e);
    }

    if (!kmlFile) {
      alertWarn('Map has not been made for this mission!');

      return;
    }

    let kmltext = '';

    if (kmlFile.filename.endsWith('.kmz')) {
      kmltext = await kmz2kml(kmlFile.content as Blob);
    } else {
      kmltext = await kmlFile.text();
    }

    const dealSitesType = job.getFirst('Deal Sites Type') ?? '';
    const expectedDensity = job.getFirst('Deal Density') ?? undefined;

    return await session.loadKml(kmltext, jobID, {
      sampling,
      // TODO figure out this typing
      dealSitesType: dealSitesType as JobType | undefined,
      fieldName: job.get('Field Name Clean'),
      expectedDensity,
    });
  }

  async handleNonSamplingCase(
    dataSource: DataSourceOption,
    session: Session,
    job: LocalAtRecord,
    jobID: string,
    sampling: boolean,
    setAutogenHelper?: ShowAutoGenHelper,
  ): Promise<Mission | undefined> {
    if (dataSource === 'Shapefiles') {
      return await this.loadFromShapefiles(session, job, jobID, setAutogenHelper);
    }

    let kmltext = '';
    const fieldAttachments = await job.get_attachments(dataSource);
    const kmlFile = fieldAttachments[0];

    if (kmlFile.filename.endsWith('.kmz')) {
      kmltext = await kmz2kml(kmlFile.content as Blob);
    } else {
      kmltext = await kmlFile.text();
    }

    return await session.loadKml(kmltext, jobID, { sampling });
  }

  async loadFromShapefiles(
    session: Session,
    job: LocalAtRecord,
    jobId: string,
    setAutogenHelper: ShowAutoGenHelper | undefined,
  ): Promise<Mission | undefined> {
    const shpfiles: RogoShapefile[] = [];

    const loadAttachments = async (field: string, type: RogoShapefileType) => {
      const attachments = await job.get_attachments(field);
      const invalidExtensions = attachments
        .map((attachment) => {
          const tokens = attachment.filename.toLowerCase().split('.');

          return tokens[tokens.length - 1];
        })
        .filter((extension) => !SupportedFileExtensions.includes(extension as any));

      if (invalidExtensions.length === 0) {
        shpfiles.push(
          ...(await Promise.all(
            attachments.map(async (attachment) => ({
              mode: type,
              data: attachment.content ? await attachment.content.arrayBuffer() : new ArrayBuffer(0),
              source: field,
              name: attachment.filename.toLowerCase(),
            })),
          )),
        );
      } else {
        alertError(`Invalid ${type} extensions: ${invalidExtensions.join(',')}`);
      }
    };

    await loadAttachments('Bnd Shp', 'bnd');
    await loadAttachments('Pts Shp', 'pts');
    await loadAttachments('Sample Zones Shp', 'pts');

    const missionName = job.get('Mission Name');

    if (shpfiles.length > 0) {
      return await session.loadShapes(missionName, shpfiles, jobId, setAutogenHelper);
    } else {
      alertWarn(`Job ${jobId} has no shapefiles, creating an empty mission.`);

      return await createMission(session, jobId, missionName);
    }
  }
}

export default OriginalMissionLoader;
